import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataService } from '../services/dataService';
import { useError } from './ErrorContext';

const TestContext = createContext();

export function useTest() {
  return useContext(TestContext);
}

export function TestProvider({ children }) {
  const [createdTests, setCreatedTests] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const { showError } = useError();
  const navigate = useNavigate();

  const loadTests = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [tests, results] = await Promise.all([
        dataService.getTests(),
        dataService.getTestResults()
      ]);
      
      setCreatedTests(tests);
      setTestResults(results);
    } catch (error) {
      console.error('Error loading tests:', error);
      if (error.message.includes('Token expired') || error.message.includes('Invalid token')) {
        localStorage.removeItem('auth_token');
        navigate('/login');
      } else {
        showError('Failed to load tests');
      }
    }
  }, [showError, navigate]);

  const deleteTest = useCallback(async (testId) => {
    try {
      await dataService.deleteTest(testId);
      setCreatedTests(prev => prev.filter(test => test._id !== testId));
      return true;
    } catch (error) {
      console.error('Error deleting test:', error);
      if (error.message.includes('Token expired') || error.message.includes('Invalid token')) {
        localStorage.removeItem('auth_token');
        navigate('/login');
      } else {
        showError('Failed to delete test');
      }
      return false;
    }
  }, [showError, navigate]);

  const createTest = useCallback(async (testData) => {
    try {
      const newTest = await dataService.createTest(testData);
      setCreatedTests(prev => [...prev, newTest]);
      return newTest;
    } catch (error) {
      console.error('Error creating test:', error);
      showError('Failed to create test');
      return null;
    }
  }, [showError]);

  const addTestResult = useCallback(async (result) => {
    try {
      const newResult = await dataService.submitTestResult(result);
      setTestResults(prev => [...prev, newResult]);
      return true;
    } catch (error) {
      console.error('Error adding test result:', error);
      showError('Failed to save test result');
      return false;
    }
  }, [showError]);

  const value = {
    createdTests,
    testResults,
    loadTests,
    createTest,
    deleteTest,
    addTestResult
  };

  return (
    <TestContext.Provider value={value}>
      {children}
    </TestContext.Provider>
  );
} 